import client from "shared/utils/axios-config";
import { BookingActivityLog, BookingDto, BookScheduleForm, InstructorSessionRow } from "shared/types";

export const getBookingsByScheduleV2 = async (
  schedule_id: string,
  occurrence_timestamp: string,
): Promise<BookingDto[]> => {
  const res = await client.get(
    `/v2/booking/schedule/${schedule_id}/${occurrence_timestamp}`,
  );
  return res.data.data;
};

export const getBookingsByCustomerV2 = async (
  customer_id: string,
  filters?: { customer_package_id?: string; instructor_id?: string },
): Promise<BookingDto[]> => {
  const res = await client.get(`/v2/booking/customer/${customer_id}`, {
    params: filters,
  });
  return res.data.data;
};

// One row per SESSION this instructor taught (or has coming up), not one
// customer's booking. All-time, no range filter (see the backend's own
// comment).
export const getSessionsByInstructorV2 = async (
  instructor_id: string,
): Promise<InstructorSessionRow[]> => {
  const res = await client.get(`/v2/booking/instructor/${instructor_id}`);
  return res.data.data;
};

export const getBookingsByCustomerPackageV2 = async (
  customer_package_id: string,
): Promise<BookingDto[]> => {
  const res = await client.get(
    `/v2/booking/customer-package/${customer_package_id}`,
  );
  return res.data.data;
};

export const bookOpenClassV2 = async (form: BookScheduleForm) => {
  const res = await client.post(`/v2/booking/`, form);
  return res.data;
};

export const updateBookingByIdV2 = async (
  bookingId: string,
  newStatus: string,
) => {
  const res = await client.put(`/v2/booking/${bookingId}`, {
    new_status: newStatus,
  });
  return res.data;
};

export const updateBookingsByIdsV2 = async (
  bookingIds: string[],
  newStatus: string,
) => {
  const res = await client.put(`/v2/booking/batch`, {
    booking_ids: bookingIds,
    new_status: newStatus,
  });
  return res.data;
};

// Undo a booking's last transition (restores its previous status + reverses the
// credit side effects). Rejected by the backend once the schedule is deleted.
export const undoBookingByIdV2 = async (
  bookingId: string,
  onConflict?: "swap" | "extend",
) => {
  const res = await client.put(
    `/v2/booking/${bookingId}/undo`,
    onConflict ? { on_conflict: onConflict } : undefined,
  );
  return res.data;
};

// One booking's full activity trail (creation + every status transition,
// including system/cron-driven ones) — see BookingActivityLog.
export const getActivityLogByBookingIdV2 = async (
  bookingId: string,
): Promise<BookingActivityLog[]> => {
  const res = await client.get(`/v2/booking/${bookingId}/activity-log`);
  return res.data.data;
};

export interface BookingRefLookupResult {
  id: string;
  schedule_id: string;
  occurrence_timestamp: string;
  // Absent from servers that predate it; treated as a regular booking.
  status?: string;
}

// Powers the booking-ref search boxes (Jadwal / Dashboard). Returns null on a
// 404 (ref not found) rather than throwing — a miss is an expected outcome
// here, not an error condition.
export const lookupBookingByRefV2 = async (
  ref: string,
): Promise<BookingRefLookupResult | null> => {
  try {
    const res = await client.get(`/v2/booking/by-ref/${ref}`);
    return res.data.data;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};
